import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { alertMonitoringApi } from '../api/adminAlert.api';
import PageHeader from '../components/PageHeader';
import DeleteAlertModal from '../components/alerts/DeleteAlertModal';
import ToastBanner from '../components/ToastBanner';
import { apiHandler, unwrapData } from '../utils/apiHandler';

function StatusBadge({ label, tone = 'gray' }) {
  const tones = {
    green: { bg: '#DCFCE7', color: '#15803D' },
    blue: { bg: '#DBEAFE', color: '#1D4ED8' },
    amber: { bg: '#FEF3C7', color: '#B45309' },
    gray: { bg: '#F1F5F9', color: '#475569' },
    red: { bg: '#FEE2E2', color: '#B91C1C' },
  };
  const t = tones[tone] || tones.gray;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        background: t.bg,
        color: t.color,
        padding: '3px 9px',
        borderRadius: 99,
        textTransform: 'capitalize',
      }}
    >
      {label}
    </span>
  );
}

function ReadField({ label, value, mono }) {
  return (
    <div>
      <div className="profile-field-label">{label}</div>
      <div
        className="profile-field-readonly"
        style={mono ? { fontFamily: "'DM Mono',monospace", fontSize: 12 } : undefined}
      >
        {value || '—'}
      </div>
    </div>
  );
}

function SectionCard({ title, icon, children, actions }) {
  return (
    <div className="profile-section-card" style={{ marginBottom: 20 }}>
      <div
        className="profile-section-title"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <span>
          <i className={`fas ${icon}`} style={{ color: '#074f78', fontSize: 14, marginRight: 6 }} />
          {title}
        </span>
        {actions}
      </div>
      {children}
    </div>
  );
}

function getBpDashboardUrl(incidentId) {
  const apiBase = process.env.REACT_APP_API_URL || '';
  const origin = apiBase.replace(/\/api\/?$/, '');
  return `${origin}/bp-dashboard?incidentId=${incidentId}`;
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

export default function AlertDetail() {
  const { alertId } = useParams();
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [toast, setToast] = useState(null);

  async function fetchAlert() {
    setLoading(true);
    const result = await apiHandler(() => alertMonitoringApi.getById(alertId));
    if (result.success) {
      setAlert(unwrapData(result));
    } else {
      setAlert(null);
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchAlert();
  }, [alertId]);

  async function handleConfirmDelete(confirmation) {
    setDeleteLoading(true);
    const result = await apiHandler(() =>
      alertMonitoringApi.deleteAlert(alertId, confirmation)
    );
    setDeleteLoading(false);
    if (result.success) {
      navigate('/alerts/history', {
        state: { toast: 'Alert deleted successfully', toastType: 'success' },
      });
      return;
    }
    setToast({ msg: result.message || 'Delete failed', type: 'error' });
  }

  if (loading) {
    return (
      <>
        <PageHeader title="Alert detail" icon="fa-bell" trail={[{ label: 'Alerts' }, { label: 'Loading…' }]} />
        <p style={{ color: '#64748B' }}>Loading alert…</p>
      </>
    );
  }

  if (!alert) {
    return (
      <>
        <PageHeader title="Alert not found" icon="fa-bell" trail={[{ label: 'Alerts' }, { label: 'Not found' }]} />
        <p style={{ color: '#64748B' }}>This alert could not be found.</p>
        <Link to="/alerts/history" style={{ color: '#074f78', fontWeight: 600 }}>
          Back to history
        </Link>
      </>
    );
  }

  const user = alert.user || {};
  const qr = user.quickResponse || {};
  const summary = alert.summary || {};

  return (
    <>
      {toast && <ToastBanner toast={toast} />}

      <PageHeader
        title={`${alert.type?.replace(/_/g, ' ') || 'Alert'} — ${alert.status?.replace(/_/g, ' ') || ''}`}
        icon="fa-bell"
        trail={[
          { label: 'Alerts', to: '/alerts/history' },
          { label: alert.id?.slice(0, 8) || 'Detail' },
        ]}
        actions={
          <>
            <button
              type="button"
              className="action-btn"
              style={{ background: '#F1F5F9', color: '#475569', border: '1px solid #E2E8F0' }}
              onClick={() => navigate(-1)}
            >
              <i className="fas fa-arrow-left" style={{ fontSize: 12 }} />
              Back
            </button>
            <button
              type="button"
              className="action-btn"
              style={{ background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA' }}
              onClick={() => setShowDeleteModal(true)}
            >
              <i className="fas fa-trash-can" style={{ fontSize: 12 }} />
              Delete
            </button>
          </>
        }
      />

      <div
        className="fade-up"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 16,
          padding: '14px 18px',
          background: '#fff',
          border: '1px solid #E8EDF5',
          borderRadius: 14,
        }}
      >
        <StatusBadge label={alert.status?.replace(/_/g, ' ') || '—'} tone="amber" />
        <span style={{ fontSize: 13, color: '#64748B' }}>Created {formatDate(alert.createdAt)}</span>
        {user.id && (
          <Link to={`/users/${user.id}`} style={{ fontSize: 13, color: '#074f78', fontWeight: 600 }}>
            View user profile
          </Link>
        )}
        {summary.recipientSummary && (
          <span style={{ fontSize: 12, color: '#64748B' }}>
            {summary.recipientSummary.notified}/{summary.recipientSummary.total} contacts notified
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SectionCard title="User" icon="fa-user">
          <div style={{ display: 'grid', gap: 14 }}>
            <ReadField label="Name" value={`${user.firstName || ''} ${user.lastName || ''}`.trim()} />
            <ReadField label="Email" value={user.email} />
            <ReadField label="Phone" value={user.mobile} mono />
            <ReadField label="XMIT number" value={qr.xmitNumber || user.xmitNumber} mono />
          </div>
        </SectionCard>

        <SectionCard title="Alert info" icon="fa-circle-info">
          <div style={{ display: 'grid', gap: 14 }}>
            <ReadField label="Type" value={alert.type?.replace(/_/g, ' ')} />
            <ReadField label="Message" value={alert.message} />
            <ReadField label="Severity" value={alert.severity} />
            <ReadField
              label="Location"
              value={
                alert.locationLat != null && alert.locationLng != null
                  ? `${alert.locationLat}, ${alert.locationLng}`
                  : null
              }
              mono
            />
            <ReadField label="Address" value={alert.locationAddress || alert.address} />
            <ReadField label="Resolved at" value={formatDate(alert.resolvedAt)} />
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Quick Response / XMIT" icon="fa-tower-broadcast">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14 }}>
          <ReadField label="XMIT number" value={qr.xmitNumber} mono />
          <ReadField label="Site num" value={qr.siteNum} mono />
          <ReadField label="Dev num" value={qr.devNum} mono />
          <ReadField label="Verified at" value={formatDate(qr.xmitVerifiedAt)} />
          <div>
            <div className="profile-field-label">Alert access</div>
            <div style={{ marginTop: 4 }}>
              <StatusBadge label={qr.alertAccess ? 'On' : 'Off'} tone={qr.alertAccess ? 'green' : 'gray'} />
            </div>
          </div>
        </div>
      </SectionCard>

      {alert.incident && (
        <SectionCard
          title="Linked incident"
          icon="fa-headset"
          actions={
            <a
              href={getBpDashboardUrl(alert.incident.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="action-btn btn-blue"
              style={{ padding: '6px 12px', fontSize: 12, textDecoration: 'none' }}
            >
              Open BP dashboard
            </a>
          }
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14 }}>
            <ReadField label="Incident ID" value={alert.incident.id} mono />
            <ReadField label="Status" value={alert.incident.status} />
            <ReadField label="Type" value={alert.incident.incidentType} />
            <ReadField
              label="Escalated to 911"
              value={alert.incident.escalatedTo911 ? 'Yes' : 'No'}
            />
            <ReadField label="Created" value={formatDate(alert.incident.createdAt)} />
          </div>
        </SectionCard>
      )}

      <SectionCard title="Trusted contacts notified" icon="fa-users">
        {(alert.recipients || []).length === 0 ? (
          <p style={{ color: '#64748B', fontSize: 13 }}>No recipients recorded.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ minWidth: 900 }}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Relationship</th>
                  <th>Status</th>
                  <th>Method</th>
                  <th>Notified</th>
                  <th>Delivered</th>
                  <th>Failed</th>
                </tr>
              </thead>
              <tbody>
                {alert.recipients.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600, fontSize: 13 }}>{r.contactName || '—'}</td>
                    <td style={{ fontSize: 12, fontFamily: "'DM Mono',monospace" }}>{r.contactPhone || '—'}</td>
                    <td style={{ fontSize: 12, color: '#64748B' }}>{r.contactEmail || '—'}</td>
                    <td style={{ fontSize: 12, color: '#64748B' }}>{r.relationship || '—'}</td>
                    <td>
                      <StatusBadge label={r.status?.replace(/_/g, ' ') || '—'} tone="gray" />
                    </td>
                    <td style={{ fontSize: 12 }}>{r.notificationMethod || '—'}</td>
                    <td style={{ fontSize: 12, color: '#64748B' }}>{formatDate(r.notifiedAt)}</td>
                    <td style={{ fontSize: 12, color: '#64748B' }}>{formatDate(r.deliveredAt)}</td>
                    <td style={{ fontSize: 12, color: '#B91C1C' }}>
                      {r.failedAt ? formatDate(r.failedAt) : '—'}
                      {r.failureReason ? ` (${r.failureReason})` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Messages" icon="fa-comment-dots">
        {(alert.messages || []).length === 0 ? (
          <p style={{ color: '#64748B', fontSize: 13 }}>No messages.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ minWidth: 800 }}>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Sender</th>
                  <th>Type</th>
                  <th>Content</th>
                  <th>Delivery</th>
                </tr>
              </thead>
              <tbody>
                {alert.messages.map((m) => (
                  <tr key={m.id}>
                    <td style={{ fontSize: 12, color: '#64748B' }}>{formatDate(m.createdAt)}</td>
                    <td style={{ fontSize: 12 }}>{m.senderName || m.senderId || '—'}</td>
                    <td style={{ fontSize: 12 }}>{m.messageType || m.type || '—'}</td>
                    <td style={{ fontSize: 12, maxWidth: 280 }}>{m.content || m.body || '—'}</td>
                    <td style={{ fontSize: 12, color: '#64748B' }}>{m.deliveryStatus || m.status || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Timeline" icon="fa-clock">
        {(alert.logs || []).length === 0 ? (
          <p style={{ color: '#64748B', fontSize: 13 }}>No log entries.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {alert.logs.map((log) => (
              <div
                key={log.id}
                style={{
                  padding: '10px 14px',
                  border: '1px solid #E8EDF5',
                  borderRadius: 10,
                  background: '#FAFBFC',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{log.action || '—'}</span>
                  <span style={{ fontSize: 12, color: '#94A3B8' }}>{formatDate(log.createdAt)}</span>
                </div>
                {log.description && (
                  <p style={{ fontSize: 12, color: '#475569', margin: '6px 0 0' }}>{log.description}</p>
                )}
                {log.details && (
                  <pre
                    style={{
                      fontSize: 11,
                      color: '#64748B',
                      margin: '8px 0 0',
                      whiteSpace: 'pre-wrap',
                      fontFamily: "'DM Mono',monospace",
                    }}
                  >
                    {typeof log.details === 'string' ? log.details : JSON.stringify(log.details, null, 2)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <DeleteAlertModal
        alert={alert}
        open={showDeleteModal}
        loading={deleteLoading}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
